import React, { useState } from 'react';
import axios from 'axios';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { API_BASE } from './scoring-types';

// Sentinel value sent to the backend when the operator wants to clear a
// car's Status back to the pre-race default (e.g. they accidentally set
// the wrong car to Queued and want to undo). The backend's
// isPreRaceStatus() accepts '---' as equivalent to the stored '"---"'
// schema default, so a car in this state will flip to On Track on its
// first transponder pass just like a brand-new row.
export const PRE_RACE_SENTINEL = '---';

// Colour coding for car status chip. Covers the original statuses plus
// the scoring-driven / terminal ones (DNS/DNF/DQ) and the pre-race
// sentinel.
export const STATUS_COLOURS: Record<string, string> = {
  [PRE_RACE_SENTINEL]: 'grey',
  'On Track':      'seagreen',
  'Finished':      'firebrick',
  'Driver Change': 'goldenrod',
  'In Pit':        'darkorange',
  'Queued':        'cadetblue',
  'Off Track':     'mediumpurple',
  'DNS':           'steelblue',
  'DNF':           '#555',
  'DQ':            'black',
};

// Statuses the operator can hand-assign from the chip menu. Ordered
// top-down as: reset (—), pre-race (Queued / DNS), racing (On Track /
// DC / In Pit / Off Track), terminal (Finished / DNF / DQ). The
// pre-race sentinel '---' lives at the top so misclicks (wrong car
// flipped to Queued, say) can be undone in one click.
export const SELECTABLE_STATUSES: readonly string[] = [
  PRE_RACE_SENTINEL,
  'Queued',
  'DNS',
  'On Track',
  'Driver Change',
  'In Pit',
  'Off Track',
  'Finished',
  'DNF',
  'DQ',
];

// Cars.Status is stored with a literal '"---"' default in the DB schema
// (quotes and all). Display as a clean em-dash; isPreRaceStatus on the
// backend already treats both forms as pre-race.
export function displayStatusLabel(status: string | null | undefined): string {
  if (status == null) return '—';
  const trimmed = status.trim();
  if (trimmed === '' || trimmed === '"---"' || trimmed === '---') return '—';
  return trimmed;
}

/**
 * Clickable status chip. Opens a menu of all SELECTABLE_STATUSES on
 * click; selecting one POSTs { CarNumber, Status } to /api/setStatus.
 *
 * We deliberately do not optimistically update any local state — the
 * backend's setStatus route publishes on 'status' and calls
 * broadcastLiveBoardRow, which in turn publishes on 'liveBoard' and
 * 'allCarsBoard'. Every page that subscribes to those channels picks up
 * the change automatically, keeping all tabs / pages in sync with a
 * single source of truth.
 */
export function StatusChip({
  carNumber,
  status,
}: {
  carNumber: number;
  status: string | null;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const label = displayStatusLabel(status);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (submitting) return;
    setErr(null);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (next: string) => {
    handleClose();
    if (next === status) return;
    setSubmitting(true);
    setErr(null);
    try {
      await axios.post(`${API_BASE}/api/setStatus`, {
        CarNumber: carNumber,
        Status:    next,
      });
    } catch (e) {
      const message =
        axios.isAxiosError(e) && e.response?.data
          ? typeof e.response.data === 'string'
            ? e.response.data
            : (e.response.data as { error?: string }).error ?? JSON.stringify(e.response.data)
          : (e as Error).message;
      setErr(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Chip
        label={submitting ? '…' : label}
        size="small"
        onClick={handleOpen}
        sx={{
          bgcolor: STATUS_COLOURS[label] ?? 'slategrey',
          color: 'white',
          fontWeight: 500,
          cursor: submitting ? 'wait' : 'pointer',
          '&:hover': { filter: 'brightness(1.15)' },
        }}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {SELECTABLE_STATUSES.map((opt) => {
          const optLabel = displayStatusLabel(opt);
          const isCurrent = optLabel === label;
          return (
            <MenuItem
              key={opt}
              selected={isCurrent}
              onClick={() => handleSelect(opt)}
              sx={{ py: 0.5 }}
            >
              <Chip
                label={optLabel}
                size="small"
                sx={{
                  bgcolor: STATUS_COLOURS[opt] ?? 'slategrey',
                  color: 'white',
                  fontWeight: 500,
                  minWidth: 120,
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
      {err && (
        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
          {err}
        </Typography>
      )}
    </>
  );
}

export default StatusChip;
